import { AnalyticsEventTopic } from '~/modules/analytics/analyticsHelper';
import { FeatureFlags } from '~/modules/feature_flags';
import { aiSearchAnalyticsStore } from '~/modules/stores/aiSearchAnalyticsStore';
import { SearchTotals } from '~/modules/stores/searchStore';
import { AI_RELATED_SEARCH_SOURCE, AI_RELATED_SEARCH_TYPE } from './aiSearchConstants';
import { GA4AiSearchEvent, GtmEventType } from './gtmEventType';

interface AiRelatedSearchClickEvent {
    topic: string;
    searchContext?: {
        queryText?: string;
        totals?: SearchTotals;
        searchSource?: string;
    };
}

/**
 * GA4 event: `custEv_search` fired when the resulting search completes after
 * a user clicks a related-search (NBQ) pill inside the AI Overview. Matches
 * the same `ANALYTICS_SEARCH_DONE` topic as {@link GtmSearchType} but narrows
 * on `searchSource=aiRelatedSearchPill`, which is stamped onto the URL by
 * `search.handleAiSuggestedSearchClick`.
 *
 * `position` and `aiSummaryCount` come from `aiSearchAnalyticsStore` — the
 * pill component writes them at click-time, this handler consumes-and-clears
 * them here so a duplicate search-done event does not re-fire with stale
 * values. Keeps view-transient analytics data out of the URL / SearchParams.
 *
 * MUST be registered before {@link GtmSearchType} in {@link GtmEventTypeFactory}
 * — otherwise the more permissive handler swallows this event.
 */
export class GtmAiRelatedSearchClickType extends GtmEventType {
    canHandle(event: AiRelatedSearchClickEvent): boolean {
        return (
            event.topic === AnalyticsEventTopic.ANALYTICS_SEARCH_DONE &&
            event.searchContext?.searchSource === AI_RELATED_SEARCH_SOURCE &&
            FeatureFlags.FF_ACE_ENABLE_AI_LISTINGS_SUMMARY_ANALYTICS_EVENTS
        );
    }

    getBody(event: AiRelatedSearchClickEvent): GA4AiSearchEvent {
        const totals = event.searchContext?.totals;
        const apps = totals?.apps || 0;
        const consultants = totals?.consultants || 0;
        const agents = totals?.agents || 0;
        const slack = totals?.slack || 0;
        const externalAgents = totals?.externalAgents || 0;
        // Salesforce platform = Apps, mirroring platformFilter.ts and the sibling
        // AI-search impression handler. Agents are a `tech=force/copilot` subset of
        // apps, so they are not added again here.
        const salesforce = apps;
        const pending = aiSearchAnalyticsStore.consumePending();
        // `ai_summary_results` is the count of AI-summary listings OR related
        // AI-search suggestions shown to the user; for a pill-initiated search
        // we capture the pill count at click time and stash it in
        // aiSearchAnalyticsStore so the value survives the aiOverviewStore
        // reset that fires before this event.
        const aiSummaryResults = pending?.aiSummaryCount ?? 0;

        // Field order mirrors the spec's dataLayer example verbatim — Merkle
        // reads these top-to-bottom when validating in the tag manager, so
        // preserving order avoids a visual-diff false positive.
        return {
            event: 'custEv_search',
            search_term: event.searchContext?.queryText || '',
            search_type: AI_RELATED_SEARCH_TYPE,
            search_result_count: apps + consultants + agents + slack + externalAgents,
            position: pending?.position,
            search_category: 'all',
            app_results: apps,
            agent_results: agents,
            salesforce_results: salesforce,
            slack_results: slack,
            consultant_results: consultants,
            ai_summary_results: aiSummaryResults,
        };
    }
}
